import { useNavigate, useParams } from 'react-router-dom';
import './appeals.css';
import { getClaimSync, signerFor } from './store';
import { ruleForPayerName } from './payerRules';
import { billedOf, formatDateFull, money, PRACTICE, TODAY_ISO } from './utils';

/** Preview the packet (APPEALS_HANDOFF §7) — the one PDF ELVA sends. No cover sheet. */
export function PacketPrint() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const claim = claimId ? getClaimSync(claimId) : null;

  if (!claim?.packet) {
    return (
      <div className="apl apl-print" style={{ padding: 40 }}>
        <p>This packet isn’t available.</p>
        <button type="button" className="apl-btn" onClick={() => { void navigate('/denied'); }}>Back</button>
      </div>
    );
  }

  const rule = ruleForPayerName(claim.payer.name);
  const address = rule.address;
  const billed = billedOf(claim.procedures);
  const decisionDate = claim.deniedDate ?? claim.paidDate ?? '';
  const pages = claim.packet.reduce((s, d) => s + d.pages, 0);
  const clinical = claim.appealKind === 'clinical';
  // Clinical appeals are signed by the treating dentist; billing/patient appeals
  // by the practice's chosen billing signer (D130) — never whoever treated.
  const signer = signerFor(claim);
  const signerName = signer.replace('Dr. ', '');

  return (
    <div className="apl apl-print">
      <div className="apl-print-controls">
        <button type="button" className="apl-btn" onClick={() => { void navigate(`/denied/${claim.id}`); }}>Back</button>
        <button type="button" className="apl-btn apl-btn-primary" onClick={() => { window.print(); }}>Download PDF</button>
      </div>

      {/* Page 1 — the letter */}
      <section className="apl-sheet">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700 }}>{PRACTICE}</div>
            <div className="muted">[Practice address]</div>
            <div className="muted">[Practice phone]</div>
          </div>
          <div>{formatDateFull(TODAY_ISO)}</div>
        </div>

        <p style={{ marginTop: 20, whiteSpace: 'pre-line' }}>
          {address ?? `${claim.payer.short} Dental, the appeals address on ${claim.payer.short}’s denial letter of ${decisionDate}`}
        </p>

        <div className="apl-regrid">
          <div className="muted">Patient</div><div>{claim.patient} · member {claim.memberId}</div>
          <div className="muted">Date of service</div><div>{claim.serviceDate}</div>
          <div className="muted">Procedures</div><div>{claim.procedures.map((p) => `${p.code}${p.tooth ? ` ${p.tooth}` : ''}`).join(', ')}</div>
          <div className="muted">Billed</div><div>{money(billed)}</div>
          <div className="muted">{claim.payer.short}’s decision</div><div>{decisionDate}{claim.why.metaLine ? ` · ${claim.why.metaLine.split('·')[0]?.trim() ?? ''}` : ''}</div>
        </div>

        <p style={{ whiteSpace: 'pre-line', marginTop: 16 }}>{claim.letter?.text}</p>

        <p style={{ marginTop: 20 }}>Sincerely,</p>
        <div style={{ marginTop: 10, fontStyle: 'italic', fontSize: 20 }}>{signerName}</div>
        {clinical ? (
          <>
            <div>{signerName}, DDS · treating dentist</div>
            <div className="muted">NPI [NPI] · TIN [TIN]</div>
            <p className="muted" style={{ marginTop: 10 }}>
              {signer} reviewed and approved this letter on {claim.approvedOn ? `${claim.approvedOn}, 2026` : 'October 14, 2026'}. Sent for {PRACTICE} by ELVA, its billing service.
            </p>
          </>
        ) : (
          <>
            <div>{signerName}, DDS · for {PRACTICE}</div>
            <p className="muted" style={{ marginTop: 10 }}>Signed for {PRACTICE} by {signer}, the chosen billing signer. Sent by ELVA, its billing service.</p>
          </>
        )}

        <h3>Enclosures</h3>
        <ul className="apl-checklist">
          {claim.packet.filter((d) => !d.isLetter).map((d) => (
            <li key={d.n}>{d.name}</li>
          ))}
        </ul>
      </section>

      {/* Page 2 — the payer's own form, filled in by ELVA */}
      <section className="apl-sheet">
        <h2>{claim.payer.short} — {rule.payer === 'Aetna' ? 'Provider Complaint and Appeal Form' : 'Payer appeal form'}</h2>
        <p className="muted">Filled in by ELVA{clinical ? ` · signed with ${signer}’s signature on file` : ` · signed for ${PRACTICE} by ${signer}, signature on file`}</p>
        <div className="apl-regrid" style={{ marginTop: 16 }}>
          <div className="muted">Provider</div><div>{claim.provider}, DDS · NPI [NPI] · TIN [TIN]</div>
          <div className="muted">Patient</div><div>{claim.patient} · member {claim.memberId}</div>
          <div className="muted">Claim</div><div>{claim.id} · service {claim.serviceDate}</div>
          <div className="muted">Procedures</div><div>{claim.procedures.map((p) => `${p.code}${p.tooth ? ` ${p.tooth}` : ''}`).join(', ')}</div>
          <div className="muted">Amount</div><div>{money(billed)}</div>
          <div className="muted">Request</div><div>☑ Appeal of a denied or reduced claim</div>
          <div className="muted">Reason</div><div>{claim.why.headline}</div>
          <div className="muted">Signature</div><div style={{ fontStyle: 'italic' }}>{signerName} · {formatDateFull(TODAY_ISO)}</div>
        </div>
      </section>

      {/* Pages 3+ — the attached documents */}
      {claim.packet.filter((d) => !d.isLetter).map((d) => (
        <section className="apl-sheet" key={d.n}>
          <h2>{d.name}</h2>
          <p className="muted">{d.origin}</p>
          <div style={{ marginTop: 24, border: '1px dashed #bbb', borderRadius: 8, padding: 40, textAlign: 'center', color: '#777' }}>
            {d.name} — document ({d.pages} page{d.pages > 1 ? 's' : ''})
          </div>
        </section>
      ))}

      <div className="apl-print-controls" style={{ borderTop: '1px solid #e3e8e7', borderBottom: 'none' }}>
        <span className="apl-meta">Packet · {pages} pages. No office cover sheet — ELVA sends it.</span>
      </div>
    </div>
  );
}
